import {
  FC,
  ReactNode,
  SVGProps,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Button from "@ingka/button";
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  Prompt,
  Sheets,
  Theatre,
} from "@ingka/modal";
import SSRIcon from "@ingka/ssr-icon";
import crossCircleIcon from "@ingka/ssr-icon/paths/cross-circle";
import informationIcon from "@ingka/ssr-icon/paths/information-circle";
import questionMarkIcon from "@ingka/ssr-icon/paths/question-mark-circle";
import Toast from "@ingka/toast";
import { ApiErrorType } from "@pdpp/lib-planta";

import { cx } from "../../__generated-styled-system/css";

// @todo: wait for transition to end instead of waiting an arbitrary time amount
const TRANSITION_TIME = 200;

enum PromptType {
  ERROR = -1,
  CONFIRM = 0,
  INFO = 1,
}

const getIcon = (type: PromptType): ReactNode => {
  const map: Record<PromptType, [string, () => SVGProps<SVGElement>[]]> = {
    [PromptType.ERROR]: ["#e00751", crossCircleIcon],
    [PromptType.CONFIRM]: ["#f26a2f", questionMarkIcon],
    [PromptType.INFO]: ["#f26a2f", informationIcon],
  };

  const [color, icon] = map[type];
  return <SSRIcon color={color} paths={icon} />;
};

// use timeouts to make sure we don't close the prompt while another one is being set
let promptDismissTimeout: ReturnType<typeof setTimeout>;

type ModalOptions = {
  onClose?: () => void;
  onBack?: () => void;
  size?: "small" | "large" | "medium";
  unstyled?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
};

type ModalProps<T = Record<string, unknown>> = {
  component: FC<T>;
  props: T;
  options: ModalOptions;
  visible: boolean;
  type: FC<ModalOptions & { visible: boolean; children: ReactNode }>;
};

type PromptProps = {
  summary?: ReactNode;
  title?: ReactNode;
  onClose?: () => void;
  onAccept?: () => void;
  error?: string | ApiErrorType[];
  visible?: boolean;
};

interface ModalStackValue {
  /** open a new sheet modal and add it to the stack */
  openSheet: <T>(component: FC<T>, args?: (T & ModalOptions) | string) => void;

  /** open a theatre modal */
  openTheatre: <T>(
    component: FC<T>,
    args?: (T & ModalOptions) | string,
  ) => void;

  /** close last 'amount' of sheet/theatre modals; defaults to 1 */
  closeModal: (amount?: number) => void;

  /** close all sheet/theatre modals */
  closeAllModals: () => void;

  /** sheet/theatre modal stack */
  modalStack: ModalProps[];

  /** toast, there can be only one */
  toast?: string;

  /** set current toast message */
  setToast: (text: string) => void;

  /** prompt modal - no stack, there can be only one */
  prompt: false | PromptProps;

  /** open or close prompt alert ('false' to close) */
  setPrompt: (newPrompt: PromptProps | string | false) => void;
}

const ModalStackContext = createContext<ModalStackValue>({} as ModalStackValue);

const TheatreModal = (
  props: ModalOptions & { children: ReactNode; visible: boolean },
): ReactNode => {
  const { closeModal, modalStack, closeAllModals } =
    useContext(ModalStackContext);
  const {
    onClose = closeModal,
    onBack,
    visible = true,
    header,
    footer,
    children,
    unstyled,
  } = props;

  const closeHandler = (): void => {
    closeAllModals();
    if (onClose !== undefined) {
      onClose();
    }
  };

  const backHandler = (): void => {
    closeModal();
    if (onBack) {
      onBack();
    }
  };

  const headerNode =
    typeof header === "string" ? (
      <ModalHeader
        title={header}
        backBtnClick={modalStack.length > 1 ? backHandler : undefined}
        closeBtnClick={() => onClose()}
      />
    ) : (
      header
    );

  return (
    <Modal
      className="content-box"
      visible={visible}
      handleCloseBtn={closeHandler}
    >
      <Theatre header={<></>}>
        {unstyled !== undefined && unstyled ? (
          children
        ) : (
          <div className="container">
            <div className="header">{headerNode}</div>
            <div className="scrollable-content">{children}</div>
            {footer != null && <div className="footer">{footer}</div>}
          </div>
        )}
      </Theatre>
    </Modal>
  );
};

const PromptModal = (props: PromptProps): ReactNode => {
  const { t } = useTranslation();
  const { setPrompt } = useContext(ModalStackContext);
  const { visible = true, title, onAccept, onClose, error, summary } = props;
  const promptType =
    error != null
      ? PromptType.ERROR
      : onAccept != null
        ? PromptType.CONFIRM
        : PromptType.INFO;

  const closeAction = (): void => {
    if (onClose) {
      onClose();
    }

    setPrompt(false);
  };

  const acceptAction = (): void => {
    if (onAccept) {
      onAccept();
      setPrompt(false);
    } else {
      closeAction();
    }
  };

  return (
    <Modal visible={visible} escapable={false} handleCloseBtn={closeAction}>
      <Prompt
        title=""
        titleId="modal-stack"
        className={cx(
          "popup-alert",
          promptType === PromptType.CONFIRM ? "cautionary" : "negative",
        )}
        header={null}
        footer={
          <ModalFooter>
            {onAccept && (
              <Button
                text={t("action.cancel")}
                onClick={closeAction}
                type="secondary"
                size="small"
                className="mt-3"
              />
            )}
            <Button
              text={t("action.ok")}
              onClick={acceptAction}
              type="primary"
              size="small"
              className="mt-3"
            />
          </ModalFooter>
        }
      >
        <div className="message">
          <div className="icon">{getIcon(promptType)}</div>
          <div className="text">
            <h3>{title}</h3>

            {summary != null && summary}
          </div>
        </div>
      </Prompt>
    </Modal>
  );
};

const SheetModal = (
  props: ModalOptions & { children: ReactNode; visible: boolean },
): ReactNode => {
  const {
    onClose,
    onBack,
    unstyled = true,
    size,
    header,
    footer,
    visible,
    children,
  } = props;
  const { closeModal, closeAllModals, modalStack } =
    useContext(ModalStackContext);

  const closeHandler = (): void => {
    closeAllModals();
    if (onClose) {
      onClose();
    }
  };

  const backHandler = (): void => {
    closeModal();
    if (onBack) {
      onBack();
    }
  };

  const headerNode =
    typeof header === "string" ? (
      <ModalHeader
        title={header}
        backBtnClick={modalStack.length > 1 ? backHandler : undefined}
      />
    ) : null;

  return (
    <Modal
      className="sheet-modal"
      visible={visible}
      handleCloseBtn={closeHandler}
    >
      <Sheets
        className={cx(unstyled ? "unstyled" : "")}
        alignment="right"
        size={size}
        header={headerNode}
        footer={
          footer != null ? (
            <ModalFooter renderBorder>{footer}</ModalFooter>
          ) : null
        }
        fullSize
      >
        {unstyled ? children : <ModalBody>{children}</ModalBody>}
      </Sheets>
    </Modal>
  );
};

export function ModalStack({ children }: { children?: ReactNode }): ReactNode {
  const [modalStack, setModalStack] = useState<ModalProps[]>([]);
  const [toast, setToast] = useState<string>();
  const [toastExpired, setToastExpired] = useState(false);
  const [prompt, setPrompt] = useState<false | PromptProps>(false);

  const value = useMemo<ModalStackValue>(() => {
    function openModal<T>(
      type: ModalProps["type"],
      component: FC<T>,
      args?: (T & ModalOptions) | string,
    ): void {
      if (typeof args === "string") {
        args = { header: args } as T & ModalOptions;
      }

      // destructure args into component props and known modal options
      const { onClose, onBack, size, unstyled, header, footer, ...props } =
        args ?? {};
      const options = { onClose, onBack, size, unstyled, header, footer };
      const newEntry: ModalProps = {
        props: props as Record<string, unknown>,
        options,
        component: component as FC<Record<string, unknown>>,
        visible: true,
        type,
      };

      setModalStack((stack) => {
        // first modal in stack?
        if (!stack.length) {
          return [newEntry];
        }

        // defer adding new modal
        setTimeout(
          () => setModalStack((currentStack) => [...currentStack, newEntry]),
          TRANSITION_TIME,
        );

        // hide current modals in stack
        return [...stack.map((entry) => ({ ...entry, visible: false }))];
      });
    }

    function closeAllModals(): void {
      setModalStack((prev) => {
        // defer stack reset
        setTimeout(() => setModalStack([]), TRANSITION_TIME);

        // hide current modals in stack
        return [...prev.map((entry) => ({ ...entry, visible: false }))];
      });
    }

    return {
      modalStack,
      openSheet: (component, args) => openModal(SheetModal, component, args),
      openTheatre: (component, args) =>
        openModal(TheatreModal, component, args),
      closeModal: (amount = 1) => {
        setModalStack((prev) => {
          // defer stack pop
          setTimeout(() => {
            setModalStack((currentStack) => {
              const newStack = [...currentStack].slice(
                0,
                currentStack.length - amount,
              );

              // make last one visible again
              if (newStack.length) {
                const lastModal = newStack.at(-1);
                if (lastModal) {
                  lastModal.visible = true;
                }
              }

              return newStack;
            });
          }, TRANSITION_TIME);

          // hide all modals in stack
          return [...prev.map((entry) => ({ ...entry, visible: false }))];
        });
      },

      closeAllModals,
      toast,
      setToast,

      prompt,
      setPrompt: (newPrompt: PromptProps | string | false) => {
        const next =
          typeof newPrompt === "string" ? { title: newPrompt } : newPrompt;
        clearTimeout(promptDismissTimeout);
        setPrompt((prev) => {
          if (prev == null && next == null) {
            return false;
          }

          if (prev == null) {
            return next;
          }

          // defer showing new prompt
          promptDismissTimeout = setTimeout(
            () => setPrompt(next),
            TRANSITION_TIME,
          );

          // hide current prompt
          return { ...prev, visible: false };
        });
      },
    };
  }, [modalStack, toast, prompt]);

  // toast changed => start expiration timer
  useEffect(() => {
    if (toast == null) {
      return;
    }

    const timeout = Math.max(Math.min(toast.length * 50, 10000), 5000);
    const timer = setTimeout(() => setToastExpired(true), timeout);

    return () => {
      clearTimeout(timer);
      setToastExpired(false);
    };
  }, [toast]);

  // active toast expired => clear toast
  useEffect(() => {
    if (!toastExpired) {
      return;
    }

    const timer = setTimeout(() => setToast(undefined), TRANSITION_TIME);
    return () => {
      clearTimeout(timer);
      setToast(undefined);
    };
  }, [toastExpired]);

  return (
    <ModalStackContext.Provider value={value}>
      {children}

      {value.modalStack.map((modal, index) => (
        <modal.type key={index} {...modal.options} visible={modal.visible}>
          <modal.component {...modal.props} />
        </modal.type>
      ))}

      {value.toast != null && (
        <Toast
          text={value.toast}
          isOpen={!toastExpired}
          onCloseRequest={() => setToastExpired(true)}
          className="skapa-focus-portal"
        />
      )}

      {typeof value.prompt === "object" && <PromptModal {...value.prompt} />}
    </ModalStackContext.Provider>
  );
}

export function useModals(): ModalStackValue {
  return useContext(ModalStackContext);
}
